import React, { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import "./index.css";
import { useForm } from "react-hook-form";
import { Button, Col, Form, FormControl, InputGroup, Row, Table } from "react-bootstrap";
import moment from "moment/moment";
import { trackPromise } from "react-promise-tracker";
import { formatarParaMascaraMoedaComSeparadorDeMilhares } from "../../../../../../../components/InputTextMask";
import formatDommus from "../../../../../../../helpers/format";
import RenegociacaoService from "../../../../../../../services/RenegociacaoService";
import { Parcelas, SimuladorFinanceiroProps } from "../types";
import Swal from "sweetalert2";
import GuService from "../../../../../../../services/GuService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMoneyBillWave, faCreditCard, faDollarSign } from "@fortawesome/free-solid-svg-icons";

const TIPO_REGRA_POR_PAGAMENTO: Record<string, string> = {
  cartao_credito: "cartao",
  boleto_bancario: "boleto",
  pix: "pix",
};

export function SimuladorFinanceiro({ parcelas, closeModal, negociacaoParametros, ticket, dataCorte, abrirModalValidacao: _abrirModalValidacao, parcelasLoading }: SimuladorFinanceiroProps) {
  const { register, setValue, handleSubmit, watch, errors, clearErrors, getValues } = useForm();
  let tipoPagamentoWatch = watch("tipo_pagamento");
  const parcelaWatch = watch("parcela");
  const [percentualEntrada, setPercentualEntrada] = useState<number>(0);
  const [descontoValor, setDescontoValor] = useState<number>(0);
  const [percentualDescontos, setPercentualDescontos] = useState<{ encargos?: number; valor?: number }>({});
  const [valorFinalSaldoDevedor, setValorFinalSaldoDevedor] = useState<number>(0);
  const [optionsParcelas, setOptionsParcelas] = useState<JSX.Element[]>([]);
  const percentuaisEntrada = useMemo<number[]>(() => [15, 20, 30, 40, 50, 70], []);
  const [selectedAtrasada, setSelectedAtrasada] = useState<Parcelas[]>([]);
  const [selectedAVencer, setSelectedAVencer] = useState<Parcelas[]>([]);
  const [modoPercentual, setModoPercentual] = useState(false);
  const [tipoDesconto, setTipoDesconto] = useState<string>("valor");
  const [dataVencimento, setDataVencimento] =  useState<boolean>(false);
  const hoje = new Date().toISOString().split("T")[0];
  const [checkEncargos, setCheckEncargos] = useState<boolean>(false);
  const [descontoEncargoParcela, setDescontoEncargoParcela] = useState<boolean>(false);
  // total que será renegociado
  const [totalRenegociar, setTotalRenegociar] = useState<number>(0);
  

  // listas locais de parcelas (inicializa a partir da prop 'parcelas')
  const [parcelasAtrasadas, setParcelasAtrasadas] = useState<Parcelas[]>(() => {
    const data = parcelas?.atrasada;
    const arr = data ?? [];
    return arr.map((p, idx) => ({ ...p, id: p?.id ?? -(idx + 1) }));
  });

  const [parcelasAVencer, setParcelasAVencer] = useState<Parcelas[]>(() => {
    const data = parcelas?.avencer;
    const arr = data ?? [];
    return arr.map((p, idx) => ({ ...p, id: p?.id ?? -(idx + 1) }));
  });

  // sincroniza listas locais quando a prop 'parcelas' for atualizada pelo pai
  useEffect(() => {
    const atrasada = parcelas?.atrasada ?? [];
    setParcelasAtrasadas(atrasada.map((p: any, idx: number) => ({...p, id: p?.id ?? -(idx + 1)})));

    const avencer = parcelas?.avencer ?? [];
    setParcelasAVencer(avencer.map((p: any, idx: number) => ({ ...p, id: p?.id ?? -(idx + 1) })));
    
    setSelectedAtrasada([]);
    setSelectedAVencer([]);
  }, [parcelas]);

  const formatBRL = (v?: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v ?? 0);

  const getValorParcela = (p?: Parcelas | null): number => {
    return Number(p?.valor_corrigido) || Number(p?.valor) || 0;
  };

  // soma das parcelas selecionadas (atrasadas + a vencer)
  const totalSelecionado = useMemo(() => {
    const sum = (items: Parcelas[] = []) => items.reduce((acc, cur) => acc + getValorParcela(cur), 0);
    return sum(selectedAtrasada) + sum(selectedAVencer);
  }, [selectedAtrasada, selectedAVencer]);

  // --- totais por tabela ---
  const totalSelecionadoAtrasadas = useMemo(() => {
    return (selectedAtrasada || []).reduce((acc, cur) => acc + getValorParcela(cur), 0);
  }, [selectedAtrasada]);

  const totalEncargosSelecionado = useMemo(() => {
    return (selectedAtrasada || []).reduce((acc, cur) => {
      return Number(acc + Number(cur.valor_juros_multa) || 0);
    }, 0);
  }, [selectedAtrasada]);

  const totalSelecionadoAVencer = useMemo(() => {
    return (selectedAVencer || []).reduce((acc, cur) => acc + getValorParcela(cur), 0);
  }, [selectedAVencer]);

  // desconto em reais calculado
  const descontoEmReais = useMemo(() => {
    const desconto = Number(descontoValor) || 0;
    if (tipoDesconto === "percentual") {
      const baseCalculo = checkEncargos
        ? Number(totalEncargosSelecionado) || 0
        : Number(totalRenegociar) || 0;

      return (desconto / 100) * baseCalculo;
    }
    return desconto;
  }, [descontoValor, tipoDesconto, totalRenegociar, totalEncargosSelecionado, checkEncargos]);

  // quando o tipo de desconto NÃO for percentual, limpamos o valor de entrada e percentual
  useEffect(() => {
    if (tipoDesconto !== 'percentual') {
      setValue("valor_entrada", "");
      setPercentualEntrada(0);
    }
  }, [tipoDesconto, setValue]);

  // total após aplicar desconto (base para cálculos de entrada e opções de parcelas)
  const totalParaCalculos = useMemo(() => {
    const val = (Number(totalRenegociar) || 0) - (Number(descontoEmReais) || 0);
    return val > 0 ? val : 0;
  }, [totalRenegociar, descontoEmReais]);
  
  const visibleAtrasadas = useMemo(() => parcelasAtrasadas, [parcelasAtrasadas]);
  const visibleAVencer = useMemo(() => parcelasAVencer, [parcelasAVencer]);

  const allVisibleAtrasadasSelected = visibleAtrasadas.length > 0 && visibleAtrasadas.every(v => !!selectedAtrasada.find(s => s?.id === v?.id));
  const allVisibleAVencerSelected = visibleAVencer.length > 0 && visibleAVencer.every(v => !!selectedAVencer.find(s => s?.id === v?.id));

  // Calcula desconto sobre encargos
  const calculaDescontoEncargos = useCallback((percentualDesconto = 0): number => {
    return (totalEncargosSelecionado * percentualDesconto) / 100;
  }, [totalEncargosSelecionado]);

  // Saldo base do parcelamento: total selecionado menos desconto, desconto de encargos e entrada.
  // Nunca retorna valor negativo — descontos somados podem ultrapassar o total.
  const calcularSaldoParcelamento = useCallback((percentualDescontoEncargos = 0): number => {
    const entradaInformada = formatDommus.formatarDeMascaraMoeda(getValues("valor_entrada") ?? "") ?? 0;
    const saldo =
      (Number(totalRenegociar) || 0)
      - (Number(descontoEmReais) || 0)
      - calculaDescontoEncargos(percentualDescontoEncargos)
      - (Number(entradaInformada) || 0);

    return saldo > 0 ? saldo : 0;
  }, [totalRenegociar, descontoEmReais, calculaDescontoEncargos, getValues]);

  // desk e gestão de usuários usam vocabulários diferentes para a forma de pagamento
  const tipoRegraSelecionada = TIPO_REGRA_POR_PAGAMENTO[tipoPagamentoWatch] ?? null;

  const filtrarParametrosPorTipoPagamento = useCallback((parametro: any) =>
    !!tipoPagamentoWatch && (!parametro?.tipoPagamento || parametro.tipoPagamento === tipoPagamentoWatch),
  [tipoPagamentoWatch]);

  const regraSeAplica = useCallback((regra: any) => {
    if (regra?.data_limite && moment(regra.data_limite).endOf("day") < moment()) {
      return false;
    }

    // PIX e À Vista não dependem de faixa de valor
    if (regra?.tipo === "pix" || regra?.tipo === "a_vista") {
      return true;
    }

    const saldo = totalRenegociar ?? 0;
    const minimo = Number(regra?.valor_minimo);
    const maximo = Number(regra?.valor_maximo);

    return (!minimo || saldo >= minimo) && (!maximo || saldo <= maximo);
  }, [totalRenegociar]);

  const regrasDaFormaSelecionada = useMemo(() => {
    if (!tipoRegraSelecionada) {
      return [];
    }

    // antes da opção de boleto existir no GU, regra sem tipo ou "parcelado" era o cartão
    const tipoDaRegra = (regra: any) => (!regra?.tipo || regra.tipo === "parcelado") ? "cartao" : regra.tipo;

    return (negociacaoParametros?.parametro ?? []).filter((regra: any) => tipoDaRegra(regra) === tipoRegraSelecionada);
  }, [negociacaoParametros, tipoRegraSelecionada]);

  const regraAplicavel = useMemo(() => {
    return regrasDaFormaSelecionada.find(regraSeAplica) ?? null;
  }, [regrasDaFormaSelecionada, regraSeAplica]);

  const regraAVista = useMemo(() => {
    return negociacaoParametros?.parametro?.find((regra: any) => regra?.tipo === "a_vista" && regraSeAplica(regra));
  }, [negociacaoParametros, regraSeAplica]);

  const percentualEntradaMinimo = useMemo(() => {
    const percentuais = (regraAplicavel?.parametros ?? [])
      .filter(filtrarParametrosPorTipoPagamento)
      .map((item: any) => Number(item?.percent_entrada))
      .filter((percentual: number) => Number.isFinite(percentual) && percentual > 0);

    return percentuais.length ? Math.min(...percentuais) : 0;
  }, [regraAplicavel, filtrarParametrosPorTipoPagamento]);

  const valorEntradaMinimo = ((totalParaCalculos ?? 0) * percentualEntradaMinimo) / 100;

  const entradaIntegral = Math.round(percentualEntrada) === 100;

  const validarEntradaMaxima = useCallback((valorEntradaMascarado: string) => {
    const valor = formatDommus.formatarDeMascaraMoeda(valorEntradaMascarado ?? "") ?? 0;
    if (valor <= totalParaCalculos) {
      return true;
    }

    Swal.fire({
      titleText: "Atenção",
      text: `O valor da entrada não pode ser maior que o total a renegociar (${formatBRL(totalParaCalculos)}).`,
      icon: "warning",
    });

    setValue("valor_entrada", "");
    setPercentualEntrada(0);

    return false;
  }, [totalParaCalculos, setValue]);

  const validarEntradaMinima = useCallback((valorEntradaMascarado: string) => {
    const valor = formatDommus.formatarDeMascaraMoeda(valorEntradaMascarado ?? "") ?? 0;
    // tolerância de 1 centavo para arredondamento do cálculo percentual
    if (!valor || !percentualEntradaMinimo || valor + 0.01 >= valorEntradaMinimo) {
      return true;
    }

    Swal.fire({
      titleText: "Entrada abaixo do mínimo",
      text: `A regra configurada nos parâmetros exige entrada mínima de ${percentualEntradaMinimo}% do saldo, ou seja, ${formatBRL(valorEntradaMinimo)}.`,
      icon: "warning",
    });

    return false;
  }, [percentualEntradaMinimo, valorEntradaMinimo]);

  const toggleSelectedAtrasada = (p: Parcelas) => {
    setSelectedAtrasada(prev => {
      const exists = prev.find(item => item?.id === p?.id);
      if (exists) return prev.filter(item => item?.id !== p?.id);
      return [...prev, p];
    });
  };

  const toggleSelectedAVencer = (p: Parcelas) => {
    setSelectedAVencer(prev => {
      const exists = prev.find(item => item?.id === p?.id);
      if (exists) return prev.filter(item => item?.id !== p?.id);
      return [...prev, p];
    });
  };

  // seleção/desseleção de todas as visíveis
  const toggleSelectAllVisibleAtrasadas = (checked: boolean) => {
    if (checked) {
      setSelectedAtrasada(prev => {
        const ids = new Set(prev.map(p => p?.id));
        const toAdd = visibleAtrasadas.filter(p => !ids.has(p?.id));
        return [...prev, ...toAdd];
      });
    } else {
      const visibleIds = new Set(visibleAtrasadas.map(p => p?.id));
      setSelectedAtrasada(prev => prev.filter(p => !visibleIds.has(p?.id)));
    }
  };

  const toggleSelectAllVisibleAVencer = (checked: boolean) => {
    if (checked) {
      setSelectedAVencer(prev => {
        const ids = new Set(prev.map(p => p?.id));
        const toAdd = visibleAVencer.filter(p => !ids.has(p?.id));
        return [...prev, ...toAdd];
      });
    } else {
      const visibleIds = new Set(visibleAVencer.map(p => p?.id));
      setSelectedAVencer(prev => prev.filter(p => !visibleIds.has(p?.id)));
    }
  };

  const handleOnSubmit = (dadosRenegociacao:any) => {
    if (!validarEntradaMaxima(dadosRenegociacao?.valor_entrada) || !validarEntradaMinima(dadosRenegociacao?.valor_entrada)) {
      return;
    }

    const payloadRenegociacao = montarPayloadRenegociacao(dadosRenegociacao);
    trackPromise(RenegociacaoService.criarRenegociacao(payloadRenegociacao))
      .then((response) => {
        if(response.data.status === 'success'){
          const urlVendas = GuService.getLocalState("linkVendas");
          window.open(urlVendas + 'index_ui.php?mgr=MQ==&ui=NDc=&id_processo=' + ticket?.id_processo, '_blank');
          Swal.fire({
            titleText: "Sucesso!",
            text: "Simulação criada!",
            icon: "success",
            showConfirmButton: true
          });
          closeModal();
          window.location.href = `/?idChamado=${btoa(String(ticket?.id_ticket))}`;
        } else {
          Swal.fire({
            titleText: "Ooops...",
            text: "Erro ao simular renegociação!",
            icon: "warning",
          });
        }
      })
      .catch((error) => {
        const dadosErro = error?.response?.data;
        const mensagemBackend = dadosErro?.errors?.[0]?.message ?? dadosErro?.message;
        Swal.fire({
          titleText: "Ooops...",
          text: mensagemBackend || "Erro ao simular renegociação!",
          icon: "warning",
        });
      });
  };
  const formatarNumero = (valor: any): number => {
    if (valor === null || valor === undefined) return 0;
    return Number(String(valor).replace(',', '.'));
  };

  const montarPayloadRenegociacao = (dadosRenegociacao: any) => {
    const selecionadas = [...selectedAtrasada, ...selectedAVencer];

    const parcelasParaEnvio = selecionadas.map((p: Parcelas) => ({
      id: p?.id,
      vencimento: p?.vencimento,
      valor: getValorParcela(p),
      valor_corrigido: p?.valor_corrigido ?? null,
      chaves_externas: p?.chaves_externas ?? null,
    }));

    const quantidadeParcelas = (() => {
      try {
        return JSON.parse(dadosRenegociacao.parcela);
      } catch {
        return dadosRenegociacao.parcela;
      }
    })();

    const valorDesconto = tipoDesconto === "percentual"
      ? ((formatarNumero(descontoValor) || 0) / 100) * (formatarNumero(totalRenegociar) || 0)
      : formatarNumero(descontoValor);

    return {
      ...dadosRenegociacao,
      quantidadeParcelas: quantidadeParcelas,
      data_corte: dataCorte ?? new Date().toISOString().split("T")[0],
      valor_total: totalRenegociar,
      processo: ticket?.id_processo,
      id_ticket: ticket.id_ticket,
      valor_desconto: valorDesconto,
      parcelas: parcelasParaEnvio,
      valor_entrada: Number(String(dadosRenegociacao.valor_entrada).replace(/\./g, '').replace(',', '.')),
      desc_encargos_parcela: descontoEncargoParcela,
    };
  };

  // altera entrada baseada em percentual (base = total pós-desconto)
  const handleAlterarEntrada = (porcentagem: number) => {
    const valorEntradaPercentual = ((totalParaCalculos ?? 0) * porcentagem) / 100;
    if (valorEntradaPercentual) {
      clearErrors("valor_entrada");
    }

    setValue(
      "valor_entrada",
      formatDommus.formatarRealMoedaInput(valorEntradaPercentual.toFixed(2).toString())
    );
    setPercentualEntrada(porcentagem);
  };

  // calcula percentual a partir do valor digitado (base = total pós-desconto)
  const handleCalcularPercentualPeloValor = (valor: string) => {
    const numeric = formatDommus.formatarDeMascaraMoeda(valor);
    setPercentualEntrada(((numeric ?? 0) * 100) / (totalParaCalculos || 1));
  };

  useEffect(() => {
    // Atualiza o total a renegociar sempre que a seleção muda
    const anySelected = (selectedAtrasada && selectedAtrasada.length) || (selectedAVencer && selectedAVencer.length);
    if (anySelected) setTotalRenegociar(totalSelecionado);
    else setTotalRenegociar(0);
  }, [selectedAtrasada, selectedAVencer, totalSelecionado]);

  // Recalcula percentualEntrada automaticamente quando o total pós-desconto mudar
  useEffect(() => {
    const entradaFormatted = getValues("valor_entrada") ?? "";
    const numericEntrada = formatDommus.formatarDeMascaraMoeda(entradaFormatted) ?? 0;
    if (numericEntrada && (totalParaCalculos ?? 0) > 0) {
      const percent = (numericEntrada * 100) / (totalParaCalculos ?? 1);
      setPercentualEntrada(percent);
    }
  }, [totalParaCalculos, getValues]);

  // atualiza dataVencimento quando o modo à vista for alterado
  useEffect(() => {
    try {
      if (entradaIntegral) {
        const sinal = getValues('data_vencimento_sinal');
        setDataVencimento(!!sinal);
      } else {
        const primeira = getValues('data_vencimento_parcela');
        setDataVencimento(!!primeira);
      }
    } catch (e) {
      // ignora
      setDataVencimento(false);
    }
  }, [entradaIntegral, getValues]);

  // Atualiza opções de parcelamento (gera options) e trata regra "à vista"
  useEffect(() => {
    setValue("parcela", null);

    // Se a entrada for 100% usamos a regra 'à vista' (desconto específico)
    if (entradaIntegral) {
      if (regraAVista?.parametros?.length === 1) {
        const parametros = regraAVista.parametros[0];

        // Usamos o totalRenegociar (já reflete seleção de parcelas)
        const saldoDevedor = totalRenegociar ?? 0;
        let descontos = { encargos: 0, valor: 0 };
        let saldoFinal = saldoDevedor;

        if (parametros?.percent_desc_encargos) {
          saldoFinal = saldoFinal - calculaDescontoEncargos(parametros.percent_desc_encargos);
          descontos.encargos = parametros.percent_desc_encargos;
        }
        if (parametros?.percent_desc_valor) {
          // aplica desconto percentual sobre o valor (base = totalRenegociar)
          const valorProporcional = ((totalRenegociar ?? 0) * parametros.percent_desc_valor) / 100;
          saldoFinal -= valorProporcional;
          descontos.valor = parametros.percent_desc_valor;
        }

        setValorFinalSaldoDevedor(saldoFinal);
        setPercentualDescontos(descontos);
      } else {
        setValorFinalSaldoDevedor(0);
        setPercentualDescontos({});
        setPercentualEntrada(0);
        setValue("valor_entrada", "");
        const expirada = !regraAVista && negociacaoParametros?.parametro?.some((regra: any) => regra?.tipo === "a_vista");
        Swal.fire({
          titleText: expirada ? "Regra À Vista expirada" : "Regra À Vista não configurada",
          text: expirada
            ? "A regra expirou, configure os parâmetros."
            : "Não existe uma regra de percentual cadastrada para a modalidade \"À Vista\". Acesse a tela de parâmetros e cadastre a regra antes de prosseguir.",
          icon: "warning",
          confirmButtonText: "Entendi",
        });
      }

      // limpa opções de parcelas (não aplicável para pagamento à vista)
      setOptionsParcelas([]);
      return;
    }

    if (!regraAplicavel?.parametros?.length) {
      setOptionsParcelas([]);
      return;
    }

    const ehPix = tipoPagamentoWatch === "pix";
    const optionsParcelasTemp: JSX.Element[] = [];
    const parametrosOrdenados = [...regraAplicavel.parametros]
      .filter(filtrarParametrosPorTipoPagamento)
      // no PIX todos os parâmetros são de 1x, então vale o de maior entrada atendida
      .sort((a: any, b: any) => ehPix
        ? Number(b?.percent_entrada) - Number(a?.percent_entrada)
        : Number(a?.num_parcelas) - Number(b?.num_parcelas));

    // contagem contínua entre parâmetros: cada um define o teto da sua faixa de parcelas
    let proximaParcela = 1;

    parametrosOrdenados.forEach((item: any) => {
      // tolerância de 0,01 para arredondamento do percentual calculado a partir do valor
      if (Number(percentualEntrada) + 0.01 < Number(item?.percent_entrada ?? 0)) {
        return;
      }

      const novoSaldoDevedor = calcularSaldoParcelamento(item?.percent_desc_encargos);
      if (novoSaldoDevedor <= 0) {
        return;
      }

      const descontoEncargos = Number(item?.percent_desc_encargos) || 0;
      const limiteParcelas = ehPix ? 1 : Number(item?.num_parcelas) || 0;

      for (; proximaParcela <= limiteParcelas; proximaParcela++) {
        const valorParcela = formatarParaMascaraMoedaComSeparadorDeMilhares(novoSaldoDevedor / proximaParcela);
        const payload = JSON.stringify({
          quantidade: proximaParcela,
          valor: Number((novoSaldoDevedor / proximaParcela).toFixed(2)),
          desc_encargos: descontoEncargos
        });

        optionsParcelasTemp.push(
          <option className="option-parcelas" value={payload} key={`${regraAplicavel?.tipo || "opt"}-${proximaParcela}`}>
            {`${proximaParcela}x de R$${valorParcela} - Desconto de ${descontoEncargos}% em encargos`}
          </option>
        );
      }
    });

    setOptionsParcelas(optionsParcelasTemp);
  }, [percentualEntrada, tipoPagamentoWatch, regraAVista, regraAplicavel, setValue, calculaDescontoEncargos, calcularSaldoParcelamento, filtrarParametrosPorTipoPagamento, totalRenegociar, negociacaoParametros]);

  function validarDesconto(valorDesconto: string) {
    const limite = checkEncargos ? totalEncargosSelecionado : totalRenegociar;
    const valorDescontoFormatado = formatDommus.formatarDeMascaraMoeda(valorDesconto) ?? 0;

    if (valorDescontoFormatado > limite) {
      Swal.fire({
        titleText: "Atenção",
        text: checkEncargos
          ? "O valor do desconto não pode ser maior que o total de encargos selecionados."
          : "O valor do desconto não pode ser maior que o total de parcelas selecionadas.",
        icon: "warning",
      });

      setValue("valor_desconto", "");
      setDescontoValor(0);
      return false;
    }

    return true;
  }

  return (
    <div className="modal-simulador">
      <form onSubmit={handleSubmit(handleOnSubmit)} className="modal-simulador-content">
        <Row>
          <Col md={12}>
            <div className="p-3">
              <h6>Parcelas Atrasadas</h6>
              <div className="table-container">
                <Table bordered hover size="sm" striped className="table-simulador">
                  <thead>
                    <tr>
                      <th style={{ width: "4%" }}>
                        {Array.isArray(parcelasAtrasadas) && parcelasAtrasadas.length > 0 && (
                          <Form.Check type="checkbox" disabled={parcelasLoading} checked={allVisibleAtrasadasSelected} onChange={(e) => toggleSelectAllVisibleAtrasadas(e.target.checked)} aria-label="Selecionar todas as visíveis" />
                        )}
                      </th>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Número da Parcela</th>
                      <th>Valor Principal</th>
                      <th>Valor Atualizado</th>
                      <th>Encargos</th>
                      <th>Vencimento</th>
                    </tr>
                  </thead>
                   <tbody>
                    {parcelasLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center small text-muted">Carregando parcelas...</td>
                      </tr>
                    ) : ((!Array.isArray(parcelasAtrasadas) || parcelasAtrasadas.length === 0) && (
                      <tr>
                        <td colSpan={8} className="text-center small text-muted">
                          Nenhuma parcela atrasada
                        </td>
                      </tr>
                    ))}
                    {Array.isArray(visibleAtrasadas) && !parcelasLoading
                      ? visibleAtrasadas.map((p: Parcelas, idx: number) => (
                          <tr key={p?.id || idx} className="align-middle">
                            <td>
                              <Form.Check type="checkbox" disabled={parcelasLoading} checked={!!selectedAtrasada.find((x) => x?.id === p?.id)} onChange={() => toggleSelectedAtrasada(p)} aria-label={`Selecionar parcela atrasada ${p?.id}`} />
                            </td>
                            <td title={String(p.tipo ?? "-")}><small className="text-muted">{p.tipo ?? "-"}</small></td>
                            <td title={String(p.descricao ?? "-")}><small className="text-muted">{p.descricao ?? "-"}</small></td>
                            <td title={String(p.numero_parcela ?? "-")}><small className="text-muted">{p.numero_parcela ?? "-"}</small></td>
                            <td className="text-right" title={p.valor ? formatBRL(Number(p.valor)) : ""}>{p.valor && <small className="text-muted">{formatBRL(Number(p.valor))}</small>}</td>
                            <td className="text-right" title={p.valor_corrigido ? formatBRL(Number(p.valor_corrigido)) : ""}>{p.valor_corrigido && <small className="text-muted">{formatBRL(Number(p.valor_corrigido))}</small>}</td>
                            <td className="text-right" title={p.valor_corrigido != null ? formatBRL(p?.valor_juros_multa ?? 0) : ""}>
                              {(p?.valor_corrigido != null) && (
                                <small className="text-muted">{formatBRL(p?.valor_juros_multa ?? 0)}</small>
                              )}
                            </td>
                            <td title={p.vencimento ? moment(p.vencimento).format("DD/MM/YYYY") : ""}>{p.vencimento && <small className="text-muted">{moment(p.vencimento).format("DD/MM/YYYY")}</small>}</td>
                          </tr>
                        ))
                      : null}
                   </tbody>
                </Table>

                <div className="table-total-sticky" role="status" aria-live="polite">
                  <div className="total-encargos">
                    <span className="label-item">Total Encargos:</span>
                    <span className="total-value">
                      {formatBRL(totalEncargosSelecionado)}
                    </span>
                  </div>

                  <div className="total-geral">
                    <span className="label-item">Total:</span>
                    <span className="total-value">
                      {formatBRL(totalSelecionadoAtrasadas)}
                    </span>
                  </div>
                </div>
              </div>

              <h6 className="mt-3">Parcelas a Vencer</h6>
              <div className="table-container">
                <Table bordered hover size="sm" striped className="table-simulador">
                  <thead>
                    <tr>
                      <th style={{ width: "4%" }}>
                          {Array.isArray(parcelasAVencer) && parcelasAVencer.length > 0 && (
                              <Form.Check type="checkbox" disabled={parcelasLoading} checked={allVisibleAVencerSelected} onChange={(e) => toggleSelectAllVisibleAVencer(e.target.checked)} aria-label="Selecionar todas as visíveis" />
                            )}
                      </th>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th>Número da Parcela</th>
                      <th>Valor Principal</th>
                      <th>Valor Atualizado</th>
                      <th>Vencimento</th>
                    </tr>
                  </thead>
                   <tbody>
                    {parcelasLoading ? (
                      <tr>
                        <td colSpan={8} className="text-center small text-muted">Carregando parcelas...</td>
                      </tr>
                    ) : ((!Array.isArray(parcelasAVencer) || parcelasAVencer.length === 0) && (
                      <tr>
                        <td colSpan={8} className="text-center small text-muted">
                          Nenhuma parcela a vencer
                        </td>
                      </tr>
                    ))}
                    {Array.isArray(visibleAVencer) && !parcelasLoading
                      ? visibleAVencer.map((p: Parcelas, idx: number) => (
                          <tr key={p?.id || idx} className="align-middle">
                            <td>
                              <Form.Check type="checkbox" disabled={parcelasLoading} checked={!!selectedAVencer.find((x) => x?.id === p?.id)} onChange={() => toggleSelectedAVencer(p)} aria-label={`Selecionar parcela a vencer ${p?.id}`} />
                            </td>
                            <td title={String(p.tipo ?? "-")}><small className="text-muted">{p.tipo ?? "-"}</small></td>
                            <td title={String(p.descricao ?? "-")}><small className="text-muted">{p.descricao ?? "-"}</small></td>
                            <td title={String(p.numero_parcela ?? "-")}><small className="text-muted">{p.numero_parcela ?? "-"}</small></td>
                            <td className="text-right" title={p.valor ? formatBRL(Number(p.valor)) : ""}>{p.valor && <small className="text-muted">{formatBRL(Number(p.valor))}</small>}</td>
                            <td className="text-right" title={p.valor_corrigido ? formatBRL(Number(p.valor_corrigido)) : ""}>{p.valor_corrigido && <small className="text-muted">{formatBRL(Number(p.valor_corrigido))}</small>}</td>
                            <td title={p.vencimento ? moment(p.vencimento).format("DD/MM/YYYY") : ""}>{p.vencimento && <small className="text-muted">{moment(p.vencimento).format("DD/MM/YYYY")}</small>}</td>
                          </tr>
                        ))
                      : null}
                   </tbody>
                </Table>

                <div className="table-total-sticky table-right" role="status" aria-live="polite">
                  <span className="label-item">Total:</span>
                  <span className="total-value">{formatBRL(totalSelecionadoAVencer)}</span>
                </div>

              </div>
            </div>
          </Col>

          <Col md={12}>
            <div className="sim-panel">
              <div className="sim-panel-header">
                <strong className="sim-panel-title">Simular Renegociação</strong>
                <span className="sim-panel-subtitle">Informe os valores e condições para simular a renegociação.</span>
              </div>
              
              <div className="sim-top-row">
                <div className="sim-card sim-card-total">
                  <div className="sim-card-total-icon-box">
                    <FontAwesomeIcon icon={faDollarSign as IconProp} />
                  </div>
                  <div className="sim-card-total-text">
                    <span className="sim-card-total-label">Total a renegociar</span>
                    <span className="sim-card-total-value">
                      R${" "}{formatarParaMascaraMoedaComSeparadorDeMilhares(Math.max(0, (Number(totalRenegociar) || 0) - (Number(descontoEmReais) || 0)))}
                    </span>
                  </div>
                </div>

                <div className="sim-card sim-card-desconto">
                  <span className="sim-card-section-title">Descontos e encargos</span>
                  <div className="sim-desconto-row">
                    <div className="sim-desconto-field">
                      <span className="sim-label">Aplicar Desconto</span>
                      <InputGroup style={{ maxWidth: "180px" }}>
                        <InputGroup.Prepend>
                          <InputGroup.Text
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const novoModo = !modoPercentual;
                              setModoPercentual(novoModo);
                              const novoTipo = novoModo ? "percentual" : "valor";
                              setTipoDesconto(novoTipo);
                              setValue("valor_desconto", "");
                              setValue("valor_entrada", "");
                              setValue("tipo_pagamento", "");
                              setValue("parcela", "");
                              setDescontoValor(0);
                              if (clearErrors) clearErrors(["valor_desconto", "valor_entrada", "tipo_pagamento", "parcela"]);
                              setPercentualEntrada(0);
                              clearErrors && clearErrors("valor_desconto");
                            }}
                          >
                            {modoPercentual ? "%" : "R$"}
                          </InputGroup.Text>
                        </InputGroup.Prepend>
                        <FormControl
                          className="text-right"
                          aria-label="Valor do desconto"
                          name="valor_desconto"
                          ref={register}
                          onChange={(e) => {
                            if (modoPercentual) {
                              setTipoDesconto("percentual");
                              if (Number(formatDommus.formatarRealMoedaInput(e.target.value).replace(/\./g, '').replace(',', '.')) > 100) {
                                Swal.fire({ titleText: "Atenção", text: "O percentual de desconto não pode ser maior que 100%.", icon: "warning" });
                                setValue("valor_desconto", "");
                                setDescontoValor(0);
                                return;
                              }
                            } else {
                              setTipoDesconto("valor");
                            }
                            if (!validarDesconto(e.target.value)) return;
                            const formatted = formatDommus.formatarRealMoedaInput(e.target.value);
                            setValue("valor_desconto", formatted);
                            const numeric = formatDommus.formatarDeMascaraMoeda(formatted) ?? 0;
                            setDescontoValor(numeric);
                          }}
                          onBlur={() => {
                            const numeric = formatDommus.formatarDeMascaraMoeda(getValues('valor_desconto') ?? '') ?? 0;
                            setDescontoValor(numeric);
                            const entradaVal = formatDommus.formatarDeMascaraMoeda(getValues('valor_entrada') ?? '') ?? 0;
                            if (entradaVal) {
                              handleCalcularPercentualPeloValor(formatDommus.formatarRealMoedaInput(entradaVal.toFixed(2)));
                            }
                          }}
                        />
                        <input type="hidden" name="desconto_tipo" />
                      </InputGroup>
                    </div>

                    <div className="sim-desconto-field">
                      <span className="sim-label">Encargos</span>
                      <div className="encargos-container">
                        <label className={`switch ${checkEncargos ? "ligado" : "desligado"}`}>
                          <input
                            type="checkbox"
                            checked={checkEncargos}
                            onChange={(e) => setCheckEncargos(e.target.checked)}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="sim-label">{checkEncargos ? "Ativo" : "Inativo"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="sim-section">
                <div className="sim-section-header">
                  <div className="sim-section-icon-box">
                    <FontAwesomeIcon icon={faMoneyBillWave as IconProp} />
                  </div>
                  <div className="sim-section-title-block">
                    <strong>Entrada</strong>
                    <span className="sim-section-subtitle">Informe o valor e as condições da entrada.</span>
                  </div>
                </div>

                <div className="sim-entrada-grid">
                  <div className="sim-field">
                    <span className="sim-label">Valor da entrada</span>
                    <InputGroup>
                      <InputGroup.Prepend>
                        <InputGroup.Text>R$</InputGroup.Text>
                      </InputGroup.Prepend>
                      <FormControl
                        className="text-right"
                        aria-label="Valor da entrada"
                        ref={register({ required: true })}
                        name="valor_entrada"
                        onChange={(e) => setValue("valor_entrada", formatDommus.formatarRealMoedaInput(e.target.value))}
                        onBlur={(e: any) => {
                          if (!validarEntradaMaxima(e.target.value)) return;
                          handleCalcularPercentualPeloValor(e.target.value);
                          validarEntradaMinima(e.target.value);
                        }}
                      />
                    </InputGroup>
                    {percentualEntradaMinimo > 0 && (
                      <small className="form-text text-muted">
                        Entrada mínima da regra: {percentualEntradaMinimo}% ({formatBRL(valorEntradaMinimo)})
                      </small>
                    )}
                    {errors && errors.valor_entrada && <small className="form-text text-danger input-erros">É necessário um valor de entrada</small>}
                  </div>

                  <div className="sim-field">
                    <span className="sim-label">Percentuais rápidos</span>
                    <div className="sim-btns-percentuais">
                      {percentuaisEntrada.map((item, index) => (
                        <Button
                          key={index}
                          variant="secondary"
                          size="sm"
                          className={`btn-percentual-entrada ${Number(item.toFixed(2)) === Number(percentualEntrada.toFixed(2)) ? "selecionado" : ""}`}
                          onClick={() => handleAlterarEntrada(item)}
                        >
                          {item}%
                        </Button>
                      ))}
                      <Button
                        variant="secondary"
                        size="sm"
                        className={`btn-percentual-entrada ${entradaIntegral ? "selecionado" : ""}`}
                        onClick={() => handleAlterarEntrada(100)}
                      >
                        À Vista
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="sim-entrada-grid">
                  <div className="sim-field">
                    <span className="sim-label">Vencimento da Entrada</span>
                    <FormControl
                      type="date"
                      className="input-data-vencimento"
                      name="data_vencimento_sinal"
                      ref={register({ required: true })}
                      min={hoje}
                      onChange={(e) => {
                        if (entradaIntegral) setDataVencimento(!!e.target.value);
                        setValue('data_vencimento_sinal', e.target.value);
                      }}
                    />
                    {errors && errors.data_vencimento_sinal && <small className="form-text text-danger input-erros">É necessário inserir uma data</small>}
                  </div>
                  <div className="sim-field">
                    <span className="sim-label">Forma de pagamento da entrada</span>
                    <Form.Group className="mb-0">
                      <Form.Control as="select" className="select-parcelas" name="tipo_pagamento_entrada" ref={register({ required: true })}>
                        <option hidden value="">Selecione o pagamento</option>
                        <option value="pix">Pix</option>
                        <option value="cartao_credito">Cartão de Crédito</option>
                        <option value="boleto_bancario">Boleto Bancário</option>
                      </Form.Control>
                      {errors && errors.parcela_entrada && <small className="form-text text-danger input-erros">É necessário inserir uma forma de pagamento</small>}
                    </Form.Group>
                  </div>
                </div>
              </div>

              {entradaIntegral && (
                <div className="pagamento-a-vista-section">
                  <div className="valor-final-section">
                    <span className="label">Valor final (após desconto):</span>
                    <span className="valor-final">{formatBRL(valorFinalSaldoDevedor)}</span>
                  </div>
                  <div className="descontos-section">
                    {percentualDescontos?.valor ? <div>Desconto no valor: {percentualDescontos.valor}%</div> : <div>Sem desconto no valor</div>}
                    {percentualDescontos?.encargos ? <div>Desconto em encargos: {percentualDescontos.encargos}%</div> : <div>Sem desconto em encargos</div>}
                  </div>
                </div>
              )}

              {!entradaIntegral && (
                <div className="sim-section">
                  <div className="sim-section-header">
                    <div className="sim-section-icon-box">
                      <FontAwesomeIcon icon={faCreditCard as IconProp} />
                    </div>
                    <div className="sim-section-title-block">
                      <strong>Parcelas</strong>
                      <span className="sim-section-subtitle">Informe a forma de pagamento das parcelas.</span>
                    </div>
                  </div>

                  <div className="btn-tipo-pagamento-section">
                    <div className="btn-tipo-pagamento-content">
                      <input type="radio" id="checkbox-cartao-credito" ref={register({ required: true })} name="tipo_pagamento" value="cartao_credito" />
                      <label htmlFor="checkbox-cartao-credito" className={`checkbox-tipo-pagamento-label ${tipoPagamentoWatch === "cartao_credito" ? "checked" : ""}`}>
                        Cartão de Crédito
                      </label>
                    </div>
                    <div className="btn-tipo-pagamento-content">
                      <input type="radio" id="checkbox-boleto" ref={register({ required: true })} name="tipo_pagamento" value="boleto_bancario" />
                      <label htmlFor="checkbox-boleto" className={`checkbox-tipo-pagamento-label ${tipoPagamentoWatch === "boleto_bancario" ? "checked" : ""}`}>
                        Boleto
                      </label>
                    </div>
                    <div className="btn-tipo-pagamento-content">
                      <input type="radio" id="checkbox-pix" ref={register({ required: true })} name="tipo_pagamento" value="pix" />
                      <label htmlFor="checkbox-pix" className={`checkbox-tipo-pagamento-label ${tipoPagamentoWatch === "pix" ? "checked" : ""}`}>
                        Pix
                      </label>
                    </div>
                  </div>

                  {tipoPagamentoWatch && (
                    <Row>
                      <Col md={8}>
                        <span className="sim-label">Selecione a parcela desejada:</span>
                        <Form.Group controlId="parcelas.ControlSelect1" className="mb-0">
                          <Form.Control as="select" className="select-parcelas" name="parcela"
                            ref={register({ required: !entradaIntegral })}
                            onChange={(e) => {
                              const parcela = JSON.parse(e.target.value);
                              setDescontoEncargoParcela(parcela.desc_encargos || 0);
                            }}>
                            <option hidden value="">Parcelamentos disponíveis</option>
                            {optionsParcelas.map((item, index) => (
                              <Fragment key={index}>{item}</Fragment>
                            ))}
                          </Form.Control>
                          {errors?.parcela && <small className="form-text text-danger input-erros">É necessário a seleção do número de parcelas!</small>}
                          {(!optionsParcelas.length && tipoPagamentoWatch) && (
                            <small className="form-text text-danger input-erros">
                              {!negociacaoParametros?.parametro?.length
                                ? "Nenhum parâmetro de renegociação foi configurado para este empreendimento. Acesse a tela de parâmetros e cadastre as regras antes de prosseguir."
                                : !regrasDaFormaSelecionada.length
                                  ? "Nenhuma regra configurada para esta forma de pagamento."
                                  : !regraAplicavel
                                    ? "Nenhuma regra desta forma de pagamento se aplica: verifique a faixa de valor e a data limite configuradas."
                                    : `Valor de entrada mínima não atingido! Mínimo de ${percentualEntradaMinimo}% (${formatBRL(valorEntradaMinimo)}).`}
                            </small>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <span className="sim-label">Vencimento da 1ª parcela:</span>
                        <FormControl
                          type="date"
                          className="custom-input"
                          name="data_vencimento_parcela"
                          ref={register({ required: !entradaIntegral })}
                          min={hoje}
                          onChange={(e) => {
                            if (!entradaIntegral) setDataVencimento(!!e.target.value);
                            setValue('data_vencimento_parcela', e.target.value);
                          }}
                        />
                        {errors && errors.data_vencimento_sinal && <small className="form-text text-danger input-erros">É necessário inserir uma data</small>}
                      </Col>
                    </Row>
                  )}
                </div>
              )}

            </div>
          </Col>
        </Row>

        <div className="footer-section">
          <div className="total-geral-footer">Total Geral: {formatBRL(Number(totalSelecionadoAVencer) + Number(totalSelecionadoAtrasadas))}</div>
          <div className="btns-conclusao-section">
            {(() => {
              const parcelaEscolhida = !!parcelaWatch && parcelaWatch !== "";
              const podeAvancar = dataVencimento && (entradaIntegral ? true : parcelaEscolhida);
              return (
                <Button variant="success" type="submit" disabled={!podeAvancar}>
                  Avançar
                </Button>
              );
            })()}
            <Button variant="secondary" type="button" onClick={() => closeModal()}>
              Cancelar
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
